// @ts-strict-ignore
import * as Handsontable from 'handsontable';
import { ISpanData } from './span-data';
import { LabelFormat } from './label-format';
import { LabelEditorTabComponent } from './label-editor-tab/label-editor-tab.component';
import { LabelEditorUtils } from './label-editor-utils';

export class QuillTableRenderer {
  public static render(labelEditorTabComponent: LabelEditorTabComponent, hotInstance: Handsontable, td, row, column, prop, value, cellProperties) {
    const spanData: ISpanData = labelEditorTabComponent.tableProvider.getSpanData(row, column);
    // handsontable seems to call render for cells, which should not really be visible,
    // update of hidden conetent is not needed in my opinion
    if (spanData && spanData.parent) {
      return;
    }

    let rowHeight: number = 0;
    let colWidth: number = 0;
    const rowSpan: number = spanData ? spanData.rowSpan : 1;
    const colSpan: number = spanData ? spanData.colSpan : 1;
    for (let i: number = 0; i < colSpan; i++) {
      colWidth += hotInstance.getColWidth(column + i);
      if (i > 0) {
        colWidth += 1; // 1px for each possibly visible crossed column border between cells (borders between columns are always visible)
      }
    }
    for (let i: number = 0; i < rowSpan; i++) {
      // we need to reduce row height here by 1, because td elements will (potentially) use bottom border in contrast to row headers.
      // For row headers height was increased because of that by 1, this is why we need to reduce height here again for td-elements:
      rowHeight += hotInstance.getRowHeight(row + i) - 1;
    }
    // for last spanned row, we dont cross (span) last border. Such border (height 1px) will be visible. Need to substract last border:
    rowHeight--;

    const cellWidth: number = colWidth;
    const cellHeight: number = rowHeight;
    // need main container to restrict width/height so we dont get resizing of rows/columns (doing that on layoutContainer does not work)
    let mainContainer: HTMLDivElement = null;
    if (td.firstElementChild) {
      mainContainer = td.firstElementChild;
    } else {
      mainContainer = document.createElement('div');
      td.appendChild(mainContainer);
    }
    // let mainBorderWidth: number = 0;
    const mainPaddingTop: number = 2;
    const mainPaddingBottom: number = 2;
    const mainPaddingLeft: number = 4;
    const mainPaddingRight: number = 5;
    if (spanData && spanData.labelFormat.borderColor) {
      // IE11 does not support custom variables:
      // mainContainer.style.setProperty('--le-custom-border-color', spanData.labelFormat.borderColor);
      mainContainer.style.borderColor = spanData.labelFormat.borderColor;
      mainContainer.classList.remove('label-editor-no-border');
      mainContainer.classList.add('label-editor-custom-border');
    } else {
      mainContainer.classList.remove('label-editor-custom-border');
      mainContainer.classList.add('label-editor-no-border');
    }
    mainContainer.setAttribute("data-testid", "label-cell");
    const maxMultilineCount: number = spanData ? labelEditorTabComponent.tableProvider.getMaxMultilineCount(row, column) : 1;
    const cellInsets: any = LabelEditorUtils.calculateCellInsets(spanData, maxMultilineCount, cellWidth, cellHeight, rowSpan, colSpan);
    const labelCellWidth: number = cellWidth - mainPaddingLeft - mainPaddingRight - cellInsets.insetLeft - cellInsets.insetRight;
    const labelCellHeight: number = cellHeight - mainPaddingTop - mainPaddingBottom - cellInsets.insetTop - cellInsets.insetBottom;

    mainContainer.style.overflow = 'hidden';
    mainContainer.style.height = `${labelCellHeight}px`;
    mainContainer.style.maxHeight = `${labelCellHeight}px`;
    mainContainer.style.marginLeft = `${cellInsets.insetLeft}px`;
    mainContainer.style.marginRight = `${cellInsets.insetRight}px`;
    mainContainer.style.marginTop = `${cellInsets.insetTop}px`;
    mainContainer.style.marginBottom = `${cellInsets.insetBottom}px`;

    // console.log('ch: ' + row + ', ' + column + ' -> ' + labelCellHeight);

    // layout container
    let layoutContainer: HTMLDivElement = null;
    if (mainContainer.firstElementChild) {
      layoutContainer = <HTMLDivElement>mainContainer.firstElementChild;
    } else {
      layoutContainer = document.createElement('div');
      mainContainer.appendChild(layoutContainer);
    }
    layoutContainer.style.display = 'table-cell';
    layoutContainer.style.whiteSpace = 'pre';

    // text rotation
    if (!spanData || spanData.labelFormat.direction === 0) {
      layoutContainer.className = '';
      layoutContainer.style.transform = '';
      layoutContainer.style.width = `${labelCellWidth}px`;
      layoutContainer.style.height = `${labelCellHeight}px`;
    } else if (spanData.labelFormat.direction === 270) {
      layoutContainer.className = 'label-editor-transform';
      layoutContainer.style.transform = `rotate(270deg) translate(-${labelCellHeight}px, 0px)`; // for IE 11 support
      layoutContainer.style.setProperty('--le-rotate', '270deg');
      layoutContainer.style.setProperty('--le-offset-x', `-${labelCellHeight}px`);
      layoutContainer.style.setProperty('--le-offset-y', '0px');
      layoutContainer.style.width = `${labelCellHeight}px`;
      layoutContainer.style.height = `${labelCellWidth}px`;
    } else if (spanData.labelFormat.direction === 180) {
      layoutContainer.className = 'label-editor-transform';
      layoutContainer.style.transform = `rotate(180deg) translate(-${labelCellWidth}px, -${labelCellHeight}px)`; // for IE 11 support
      layoutContainer.style.setProperty('--le-rotate', '180deg');
      layoutContainer.style.setProperty('--le-offset-x', `-${labelCellWidth}px`);
      layoutContainer.style.setProperty('--le-offset-y', `-${labelCellHeight}px`);
      layoutContainer.style.width = `${labelCellWidth}px`;
      layoutContainer.style.height = `${labelCellHeight}px`;
    } else if (spanData.labelFormat.direction === 90) {
      layoutContainer.className = 'label-editor-transform';
      layoutContainer.style.transform = `rotate(90deg) translate(0px, -${labelCellWidth}px)`; // for IE 11 support
      layoutContainer.style.setProperty('--le-rotate', '90deg');
      layoutContainer.style.setProperty('--le-offset-x', '0px');
      layoutContainer.style.setProperty('--le-offset-y', `-${labelCellWidth}px`);
      layoutContainer.style.width = `${labelCellHeight}px`;
      layoutContainer.style.height = `${labelCellWidth}px`;
    }
    layoutContainer.style.minWidth = layoutContainer.style.width;
    layoutContainer.style.maxWidth = layoutContainer.style.width;

    // vertical align
    if (!spanData || spanData.labelFormat.verticalAlign === LabelFormat.VALIGN_MIDDLE) {
      layoutContainer.style.verticalAlign = 'middle';
    } else if (spanData.labelFormat.verticalAlign === LabelFormat.VALIGN_TOP) {
      layoutContainer.style.verticalAlign = 'top';
    } else if (spanData.labelFormat.verticalAlign === LabelFormat.VALIGN_BOTTOM) {
      layoutContainer.style.verticalAlign = 'bottom';
    }

    // text compression
    if (!spanData || spanData.compression === 100) {
      for (let i: number = 1; i <= 100; i++) {
        layoutContainer.classList.remove('label-editor-text-scale-' + i);
      }
      layoutContainer.classList.remove('label-editor-text-scale-right');
      layoutContainer.classList.remove('label-editor-text-scale-left');
      layoutContainer.classList.remove('label-editor-text-scale-center');
    } else {
      /* cannot do the following, because of IE 11
      layoutContainer.classList.add('label-editor-text-scale');
      const scale: string = (spanData.compression / 100).toFixed(2);
      layoutContainer.style.setProperty('--le-text-scale', scale);*/
      layoutContainer.classList.add('label-editor-text-scale-' + spanData.compression);
      // another possibility to fix alignment issue when also using compression could be to use
      // float: left or float: right in addition to text-align left/right on p-tags.
      // instead currently we use (probably better approach) of setting origin of scale-transformation:
      layoutContainer.classList.add('label-editor-text-scale-' + spanData.horizontalAlign);
    }

    // text content
    if (spanData) {
      layoutContainer.innerHTML = value;
    } else {
      layoutContainer.innerHTML = '';
    }

    if (spanData && spanData.isEndPlate) {
      // in case of end plate, any custom or error background color is ignored, instead end plate styling is applied
      mainContainer.classList.remove('label-editor-invalid-cell');
      td.classList.remove('label-editor-disabled');
      if (!spanData.labelFormat.backgroundColor || spanData.labelFormat.backgroundColor === LabelFormat.DEFAULT_BACKGROUND_COLOR) {
        mainContainer.style.backgroundColor = '';
      } else {
        mainContainer.style.backgroundColor = spanData.labelFormat.backgroundColor;
      }
      td.classList.add('label-editor-end-plate');
    } else if (cellProperties['readOnly']) {
      // in case of disabled (read-only) cells, any custom or error background color is ignored
      mainContainer.classList.remove('label-editor-invalid-cell');
      td.classList.remove('label-editor-end-plate');
      mainContainer.style.backgroundColor = '';
      td.classList.add('label-editor-disabled');
    } else if (spanData && (spanData.containsErrors || spanData.containsStrictConformityErrors)) {
      // error background color takes precedence for renderer (editor keeps custom background)
      td.classList.remove('label-editor-end-plate');
      td.classList.remove('label-editor-disabled');
      mainContainer.style.backgroundColor = '';
      mainContainer.classList.add('label-editor-invalid-cell');
    } else if (spanData && spanData.labelFormat.backgroundColor) {
      // IE 11 does not support custom variables:
      // mainContainer.classList.add('label-editor-custom-background');
      // mainContainer.style.setProperty('--le-custom-background-color', spanData.labelFormat.backgroundColor);
      td.classList.remove('label-editor-disabled');
      mainContainer.classList.remove('label-editor-invalid-cell');
      td.classList.remove('label-editor-end-plate');
      mainContainer.style.backgroundColor = spanData.labelFormat.backgroundColor;
    } else {
      td.classList.remove('label-editor-disabled');
      mainContainer.classList.remove('label-editor-invalid-cell');
      td.classList.remove('label-editor-end-plate');
      mainContainer.style.backgroundColor = '';
    }

    if (spanData && spanData.errorTooltipText) {
      td.dataset.errorTooltipText = spanData.errorTooltipText;
    } else {
      delete td.dataset.errorTooltipText;
    }

    // Handsontable.dom.empty(td);
    // mainContainer.appendChild(layoutContainer);
    // td.appendChild(mainContainer);
    return td;
  }
}
